import React, { useState, useEffect, useMemo } from 'react';
import {
	CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
	CButton, CFormLabel, CFormSelect,
} from '@coreui/react';
import { toast } from 'react-toastify';
import AxiosInstance from 'src/components/Axios';
import FormSubmitOverlay from 'src/components/myComponents/FormSubmitOverlay/FormSubmitOverlay';
import {
	invalidateCachedRequest, invalidateLettersList, invalidateCachedSession,
} from 'src/utils/supportRequestCache';

const urlApiRequests = '/support-requests';

// Поточний батько виключений зі списку опцій (вибір того самого — нікчемний),
// тож стартове значення селекту не може бути currentParentId — такої опції в списку
// вже немає, і select "мовчки" показав би не той пункт. Сентинел форсує явний вибір.
const UNSELECTED = '__unselected__';

const truncate = (text, length = 40) => {
	if (!text) return '';
	return text.length > length ? `${text.slice(0, length)}…` : text;
};

const SupportChangeParentModal = ({ open, onClose, requestId, currentParentId, sessionRequests, onSuccess }) => {
	const [selectedParentId, setSelectedParentId] = useState(UNSELECTED);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!open) return;
		setSelectedParentId(UNSELECTED);
	}, [open, currentParentId]);

	const excludedIds = useMemo(() => {
		const excluded = new Set([requestId]);
		let addedAny = true;
		while (addedAny) {
			addedAny = false;
			(sessionRequests || []).forEach(r => {
				if (!excluded.has(r.id) && r.parent_id !== null && excluded.has(r.parent_id)) {
					excluded.add(r.id);
					addedAny = true;
				}
			});
		}
		return excluded;
	}, [requestId, sessionRequests]);

	const availableParents = (sessionRequests || []).filter(r => !excludedIds.has(r.id) && r.id !== currentParentId);
	const isAlreadyRoot = currentParentId === null || currentParentId === undefined;

	const handleSubmit = async () => {
		setLoading(true);
		try {
			const newParentId = selectedParentId === '' ? null : parseInt(selectedParentId);
			await AxiosInstance.put(`${urlApiRequests}/${requestId}/parent`, { parent_id: newParentId });
			invalidateCachedRequest(requestId);
			invalidateLettersList();
			const session = (sessionRequests || []).find(r => r.id === requestId);
			if (session?.session_id) invalidateCachedSession(session.session_id);
			toast.success('Батьківський лист змінено');
			onClose();
			onSuccess({ newParentId });
		} catch (err) {
			toast.error(err.response?.data?.error || 'Помилка зміни батьківського листа');
		} finally {
			setLoading(false);
		}
	};

	return (
		<CModal visible={open} onClose={() => !loading && onClose()}>
			<CModalHeader>
				<CModalTitle>Перемістити лист #{requestId} в дереві сесії</CModalTitle>
			</CModalHeader>
			<CModalBody>
				<FormSubmitOverlay submitting={loading}>
					<CFormLabel>Новий батьківський лист</CFormLabel>
					<CFormSelect
						value={selectedParentId}
						onChange={e => setSelectedParentId(e.target.value)}
					>
						<option value={UNSELECTED} disabled hidden>— оберіть новий батьківський лист —</option>
						{!isAlreadyRoot && <option value="">— без батька (кореневий лист) —</option>}
						{availableParents.map(r => (
							<option key={r.id} value={r.id}>
								#{r.id} {r.user_email} — {truncate(r.subject || r.message)}
							</option>
						))}
					</CFormSelect>
				</FormSubmitOverlay>
			</CModalBody>
			<CModalFooter>
				<CButton
					color="primary"
					onClick={handleSubmit}
					disabled={loading || selectedParentId === UNSELECTED}
					title="Перемістити лист до обраного батьківського листа"
				>
					{loading ? 'Переміщення...' : 'Перемістити'}
				</CButton>
				<CButton
					color="secondary"
					variant="outline"
					onClick={onClose}
					disabled={loading}
					title="Скасувати"
				>
					Скасувати
				</CButton>
			</CModalFooter>
		</CModal>
	);
};

export default SupportChangeParentModal;
